import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Scroll } from "lucide-react";
import { PANCHATANTRA, PANCHATANTRA_LANGS } from "@/data/panchatantra";

const EASE = [0.16, 1, 0.3, 1];

const ACCENTS = ["#B4622D", "#1F6F6B", "#7A4E8C", "#2F5E8F", "#A03E5C"];

const IMG_ALTS = [
  {
    EN: "The scholar Vishnu Sharma telling stories to three young princes beside palm-leaf manuscripts",
    ES: "El sabio Vishnu Sharma contando cuentos a tres jóvenes príncipes",
    DE: "Der Gelehrte Vishnu Sharma erzählt drei jungen Prinzen Geschichten",
    FR: "Le sage Vishnu Sharma racontant des histoires à trois jeunes princes",
    IT: "Il saggio Vishnu Sharma racconta storie a tre giovani principi",
    PT: "O sábio Vishnu Sharma contando histórias a três jovens príncipes",
  },
  {
    EN: "The lion, hare, jackal and mongoose gazing at their golden reflections in a jungle pool",
    ES: "El león, la liebre, el chacal y la mangosta frente a sus reflejos dorados",
    DE: "Löwe, Hase, Schakal und Mungo vor ihren goldenen Spiegelbildern",
    FR: "Le lion, le lièvre, le chacal et la mangouste devant leurs reflets dorés",
    IT: "Il leone, la lepre, lo sciacallo e la mangusta davanti ai loro riflessi dorati",
    PT: "O leão, a lebre, o chacal e a mangusta diante dos seus reflexos dourados",
  },
  {
    EN: "An ancient scroll traveling golden trade routes across a magical world map",
    ES: "Un pergamino antiguo viajando por rutas doradas sobre un mapa mágico",
    DE: "Eine alte Schriftrolle reist über goldene Handelsrouten auf einer magischen Weltkarte",
    FR: "Un parchemin ancien voyageant sur des routes dorées à travers une carte magique",
    IT: "Un'antica pergamena in viaggio sulle rotte dorate di una mappa magica",
    PT: "Um pergaminho antigo viajando por rotas douradas num mapa mágico",
  },
  {
    EN: "A child reading a glowing tablet as luminous fable scenes pour out of the screen",
    ES: "Un niño leyendo una tableta luminosa de la que brotan escenas de fábulas",
    DE: "Ein Kind liest auf einem leuchtenden Tablet, aus dem Fabelwelten strömen",
    FR: "Un enfant lisant une tablette lumineuse d'où jaillissent des scènes de fables",
    IT: "Un bambino legge un tablet luminoso da cui sgorgano scene di favole",
    PT: "Uma criança lendo um tablet luminoso de onde brotam cenas de fábulas",
  },
  {
    EN: "A parent and child reading together beneath the glowing banyan tree",
    ES: "Un padre y su hijo leyendo juntos bajo el baniano luminoso",
    DE: "Eltern und Kind lesen gemeinsam unter dem leuchtenden Banyanbaum",
    FR: "Un parent et son enfant lisant ensemble sous le banian lumineux",
    IT: "Un genitore e un bambino leggono insieme sotto il baniano luminoso",
    PT: "Um pai e uma criança lendo juntos sob a baniana luminosa",
  },
];

export default function PanchatantraPage() {
  const [lang, setLang] = useState("EN");
  const navigate = useNavigate();
  const t = PANCHATANTRA[lang];

  const goLibrary = () => {
    navigate("/");
    setTimeout(() => {
      const el = document.getElementById("library");
      if (el) {
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
        else el.scrollIntoView({ behavior: "smooth" });
      }
    }, 500);
  };

  return (
    <div data-testid="panchatantra-page">
      <section className="relative flex min-h-[72vh] items-end overflow-hidden bg-emerald-ink">
        <img
          src="/spreads/panchatantra-hero.webp"
          alt="The animals of the forest gathered beneath the great banyan tree"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-ink via-emerald-ink/55 to-emerald-ink/25" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-44 sm:px-6 lg:px-8">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-gold"
            data-testid="panchatantra-eyebrow"
          >
            <span className="h-px w-10 bg-gold/70" aria-hidden="true" />
            The Source of Every Fable We Tell
          </motion.p>
          <h1
            className="mt-6 font-serif text-4xl font-bold leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl"
            data-testid="panchatantra-title"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.2, ease: EASE }}
              >
                The Panchatantra.
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block italic text-gold"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.38, ease: EASE }}
              >
                Two Thousand Years Young.
              </motion.span>
            </span>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="mt-8 max-w-2xl border-l-2 border-gold/60 pl-6"
          >
            <AnimatePresence mode="wait">
              <motion.p
                key={lang}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="font-serif text-xl italic leading-relaxed text-cream/90 sm:text-2xl"
                data-testid="panchatantra-intro"
              >
                “{t.intro}”
              </motion.p>
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <div className="sticky top-[72px] z-40 border-y border-gold/15 bg-emerald-ink/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-4 py-3 sm:px-6 lg:px-8">
          <Scroll className="h-4 w-4 shrink-0 text-gold/70" aria-hidden="true" />
          <span className="mr-2 hidden shrink-0 font-mono text-[10px] uppercase tracking-[0.24em] text-cream/50 sm:block">
            Read for parents in
          </span>
          <div role="tablist" aria-label="Essay language" className="flex gap-2" data-testid="panchatantra-lang-tabs">
            {PANCHATANTRA_LANGS.map((l) => (
              <button
                key={l.code}
                role="tab"
                aria-selected={lang === l.code}
                data-testid={`panchatantra-tab-${l.code.toLowerCase()}`}
                onClick={() => setLang(l.code)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-2 font-mono text-[11px] uppercase tracking-[0.12em] transition-all duration-300 ${
                  lang === l.code
                    ? "border-gold bg-gold text-emerald-ink"
                    : "border-cream/20 text-cream/70 hover:border-gold/50 hover:text-gold"
                }`}
              >
                <span aria-hidden="true">{l.flag}</span>
                {l.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="bg-cream py-20 text-charcoal lg:py-28">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={lang}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: EASE }}
              data-testid="panchatantra-essay"
            >
              {t.sections.map((s, i) => (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.8, delay: 0.05, ease: EASE }}
                  className={`mb-20 grid items-center gap-8 lg:gap-12 lg:grid-cols-2 ${
                    i % 2 === 1 ? "" : ""
                  }`}
                  data-testid={`panchatantra-section-${i + 1}`}
                >
                  <div
                    className={`relative ${i % 2 === 1 ? "lg:order-2" : ""}`}
                  >
                    <div
                      className="absolute -inset-2.5 rounded-sm opacity-70"
                      style={{
                        background: `linear-gradient(135deg, ${ACCENTS[i]}33, transparent 60%)`,
                      }}
                      aria-hidden="true"
                    />
                    <div
                      className="group relative overflow-hidden rounded-sm border-2 shadow-card transition-transform duration-500 hover:-translate-y-1.5 hover:shadow-lift"
                      style={{ borderColor: `${ACCENTS[i]}66` }}
                    >
                      <img
                        src={`/panchatantra/s${i + 1}.webp`}
                        alt={IMG_ALTS[i][lang] || IMG_ALTS[i].EN}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        data-testid={`panchatantra-img-${i + 1}`}
                      />
                      <div
                        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent"
                        aria-hidden="true"
                      />
                    </div>
                    <span
                      className="absolute -bottom-4 -left-3 rounded-full px-4 py-2 font-serif text-lg font-semibold italic text-cream shadow-card"
                      style={{ backgroundColor: ACCENTS[i] }}
                      aria-hidden="true"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                    <div className="flex items-center gap-3">
                      <span
                        className="h-px w-10"
                        style={{ backgroundColor: ACCENTS[i] }}
                        aria-hidden="true"
                      />
                      <span
                        className="font-mono text-[10px] uppercase tracking-[0.28em]"
                        style={{ color: ACCENTS[i] }}
                      >
                        Chapter {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h2 className="mt-4 font-serif text-2xl font-semibold leading-snug text-emerald sm:text-3xl">
                      {s.h}
                    </h2>
                    <p className="mt-4 text-lg leading-[1.85] text-charcoal/80">{s.p}</p>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mt-6 flex flex-col items-center gap-4 border border-emerald/15 bg-parchment p-10 text-center"
          >
            <p className="font-serif text-2xl italic text-emerald">
              Ready to hear the first six retellings?
            </p>
            <button
              onClick={goLibrary}
              data-testid="panchatantra-explore-btn"
              className="group flex items-center gap-2.5 rounded-full bg-emerald px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-cream transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-card"
            >
              Explore the Six Fables
              <ArrowRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
